import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getMyItems, getUserReviews, getVerificationStatus, submitIdVerification } from '../services/api';
import { Button } from '../components/ui/button';
import StarRating from '../components/StarRating';
import ItemCard from '../components/ItemCard';
import { PaymentMethodsSection } from '../components/PaymentMethodsSection';
import { User, MapPin, Mail, Phone, Star, Package, Edit, ShieldCheck, Upload, CheckCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  
  const [items, setItems] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [idVerified, setIdVerified] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [isAuthenticated, authLoading, navigate, user]);

  const fetchData = async () => {
    if (!user) return;
    try {
      const [itemsData, reviewsData, verData] = await Promise.all([
        getMyItems(),
        getUserReviews(user.id),
        getVerificationStatus()
      ]);
      setItems(itemsData);
      setReviews(reviewsData);
      setIdVerified(verData.id_verified);
      setVerificationStatus(verData.verification);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleIdUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File too large (max 10MB)'); return; }
    setUploading(true);
    try {
      const res = await submitIdVerification(file);
      setIdVerified(res.id_verified);
      toast.success(res.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-pulse text-[#596652]">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Profile Card */}
        <div className="bg-white rounded-2xl border border-[#EBE8DF] p-6 sm:p-8 mb-8">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
            {/* Avatar */}
            <div className="w-24 h-24 bg-[#8C9A76] rounded-full flex items-center justify-center flex-shrink-0">
              <User className="w-12 h-12 text-white" />
            </div>

            {/* Info */}
            <div className="flex-1 text-center sm:text-left">
              <h1 className="font-heading text-2xl font-semibold text-[#2C3329] mb-2">{user.full_name}</h1>
              
              <div className="flex flex-wrap justify-center sm:justify-start gap-4 text-[#596652] mb-4">
                <div className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  <span>{user.neighborhood}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Mail className="w-4 h-4" />
                  <span>{user.email}</span>
                </div>
                {user.phone && (
                  <div className="flex items-center gap-1">
                    <Phone className="w-4 h-4" />
                    <span>{user.phone}</span>
                  </div>
                )}
              </div>

              {/* Rating */}
              <div className="flex items-center justify-center sm:justify-start gap-2 mb-4">
                <StarRating rating={user.rating || 0} readonly />
                <span className="text-[#2C3329] font-medium">{user.rating?.toFixed(1) || '0.0'}</span>
                <span className="text-[#596652]">({user.total_reviews || 0} reviews)</span>
              </div>

              {/* Stats */}
              <div className="flex justify-center sm:justify-start gap-6">
                <div className="text-center">
                  <p className="text-2xl font-heading font-semibold text-[#2C3329]">{items.length}</p>
                  <p className="text-sm text-[#596652]">Items Listed</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-heading font-semibold text-[#2C3329]">{reviews.length}</p>
                  <p className="text-sm text-[#596652]">Reviews</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ID Verification Section */}
        <div className="bg-white rounded-2xl border border-[#EBE8DF] p-6 mb-8" data-testid="id-verification-section">
          <div className="flex items-center gap-3 mb-4">
            <ShieldCheck className={`w-6 h-6 ${idVerified ? 'text-[#4CAF50]' : 'text-[#F59E0B]'}`} />
            <h2 className="font-heading text-lg font-semibold text-[#2C3329]">Identity Verification</h2>
            {idVerified && (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#4CAF50]/10 text-[#4CAF50]" data-testid="verified-badge">
                <CheckCircle className="w-3 h-3" /> Verified
              </span>
            )}
          </div>

          {idVerified ? (
            <p className="text-[#596652] text-sm">Your identity has been verified. You can rent items on NeighborBorrow.</p>
          ) : (
            <div>
              <p className="text-[#596652] text-sm mb-4">
                Upload a photo of your government-issued ID (driver's license, passport, or state ID) to verify your identity. This is required before you can rent items.
              </p>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleIdUpload}
                accept="image/*"
                className="hidden"
                data-testid="id-upload-input"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="bg-[#D97757] text-white rounded-full hover:bg-[#C56547]"
                data-testid="upload-id-button"
              >
                {uploading ? (
                  <><Clock className="w-4 h-4 mr-2 animate-spin" /> Verifying...</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" /> Upload ID Document</>
                )}
              </Button>
            </div>
          )}
        </div>

        {/* Payment Methods Section */}
        <PaymentMethodsSection />

        {/* My Items */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-semibold text-[#2C3329]">
              <Package className="w-5 h-5 inline mr-2" />
              My Items
            </h2>
            <Link to="/list-item">
              <Button variant="outline" size="sm" className="border-[#EBE8DF] rounded-full" data-testid="profile-list-item">
                List Item
              </Button>
            </Link>
          </div>

          {items.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.slice(0, 6).map((item) => (
                <ItemCard key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <div className="text-center py-8 bg-white rounded-2xl border border-[#EBE8DF]">
              <Package className="w-10 h-10 text-[#EBE8DF] mx-auto mb-2" />
              <p className="text-[#596652]">No items listed yet</p>
            </div>
          )}
        </div>

        {/* Reviews */}
        <div>
          <h2 className="font-heading text-xl font-semibold text-[#2C3329] mb-4">
            <Star className="w-5 h-5 inline mr-2" />
            Reviews
          </h2>

          {reviews.length > 0 ? (
            <div className="space-y-4">
              {reviews.map((review) => (
                <div key={review.id} className="bg-white rounded-2xl border border-[#EBE8DF] p-4" data-testid={`review-${review.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-[#2C3329]">{review.reviewer_name}</span>
                    <StarRating rating={review.rating} readonly size="sm" />
                  </div>
                  <p className="text-[#596652]">{review.comment}</p>
                  <p className="text-xs text-[#596652]/70 mt-2">
                    {new Date(review.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 bg-white rounded-2xl border border-[#EBE8DF]">
              <Star className="w-10 h-10 text-[#EBE8DF] mx-auto mb-2" />
              <p className="text-[#596652]">No reviews yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
